import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TableMap } from '@tiptap/pm/tables';

const lockTableColumnWidthsKey = new PluginKey('lockTableColumnWidths');

const CELL_TYPES = new Set(['tableCell', 'tableHeader']);

const measureColumnWidths = (tableEl) => {
  const firstRow = tableEl.querySelector('tr');
  if (!firstRow) {
    return [];
  }

  const widths = [];
  Array.from(firstRow.children).forEach((cell) => {
    const colspan = Math.max(1, parseInt(cell.getAttribute('colspan') || '1', 10));
    const each = Math.max(48, Math.round(cell.offsetWidth / colspan));
    for (let index = 0; index < colspan; index += 1) {
      widths.push(each);
    }
  });
  return widths;
};

const tableHasUnsetColumnWidths = (tableNode) => {
  const firstRow = tableNode.firstChild;
  if (!firstRow) {
    return true;
  }

  for (let i = 0; i < firstRow.childCount; i += 1) {
    const cell = firstRow.child(i);
    const colwidth = cell.attrs.colwidth;
    const colspan = cell.attrs.colspan || 1;
    for (let j = 0; j < colspan; j += 1) {
      if (!colwidth || !colwidth[j]) {
        return true;
      }
    }
  }
  return false;
};

const lockColumnWidthsAtEvent = (view, event) => {
  const target = event.target;
  const tableEl = target?.closest?.('table');
  if (!tableEl) {
    return;
  }

  const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
  if (!coords) {
    return;
  }

  const $pos = view.state.doc.resolve(coords.inside >= 0 ? coords.inside : coords.pos);
  let tableDepth = -1;
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    if ($pos.node(depth).type.name === 'table') {
      tableDepth = depth;
      break;
    }
  }
  if (tableDepth < 0) {
    return;
  }

  const tableNode = $pos.node(tableDepth);
  if (!tableHasUnsetColumnWidths(tableNode)) {
    return;
  }

  const widths = measureColumnWidths(tableEl);
  if (!widths.length) {
    return;
  }

  const map = TableMap.get(tableNode);
  const tableStart = $pos.start(tableDepth);
  let tr = view.state.tr;
  let changed = false;

  tableNode.descendants((node, pos) => {
    if (!CELL_TYPES.has(node.type.name)) {
      return;
    }

    const col = map.colCount(pos);
    const colspan = node.attrs.colspan || 1;
    const colwidth = [];
    for (let index = 0; index < colspan; index += 1) {
      colwidth.push(widths[col + index] || 48);
    }

    const current = node.attrs.colwidth;
    const same =
      Array.isArray(current) &&
      current.length === colwidth.length &&
      current.every((value, index) => value === colwidth[index]);

    if (!same) {
      tr = tr.setNodeMarkup(tableStart + pos, undefined, {
        ...node.attrs,
        colwidth,
      });
      changed = true;
    }
  });

  if (changed) {
    view.dispatch(tr);
  }
};

const LockTableColumnWidths = Extension.create({
  name: 'lockTableColumnWidths',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: lockTableColumnWidthsKey,
        props: {
          handleDOMEvents: {
            mousedown(view, event) {
              const target = event.target;
              if (!target?.closest?.('table')) {
                return false;
              }
              lockColumnWidthsAtEvent(view, event);
              return false;
            },
          },
        },
      }),
    ];
  },
});

export default LockTableColumnWidths;
